import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdGateInfoOpenBehavior } from '../gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../chart-tabs/hd-chart-global-settings';
import { HdGateGraphContext, buildHdGateGraphPanelKey } from './hd-gate-graph.models';
import { HdGateGraphPanelComponent } from './hd-gate-graph-panel.component';

/**
 * Opent het radiale poort-paneel (de "fractal" weergave van één poort).
 *
 * Volgt hetzelfde patroon als `HdTransitShiftPopupService`: de dock-/dialogkeuze
 * komt uit de globale `gateInfoOpenBehavior`-setting, die de chart-pagina's live
 * doorgeven via {@link setOpenBehavior}. De default komt uit de opgeslagen
 * settings zodat de service ook zonder pagina-sync bruikbaar blijft.
 *
 * De tekening heeft breedte nodig en vraagt daarom altijd de `'full'`-dockzone
 * aan; pagina's waarvan de dock-host die zone niet ondersteunt degraderen dat
 * stil naar `'small'`.
 */
@Injectable({ providedIn: 'root' })
export class HdGateGraphPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /**
   * @param origin optioneel cursor-/ankerpunt voor smart-placement; zonder
   * origin landt het paneel gecentreerd.
   */
  open(
    gate: number,
    activations: readonly HdBodygraphPlanetaryActivation[],
    origin?: { x: number; y: number },
  ): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';

    this.floatingPanels.openOrFocus<HdGateGraphPanelComponent, HdGateGraphContext>({
      key: buildHdGateGraphPanelKey(gate),
      component: HdGateGraphPanelComponent,
      data: { kind: 'gate-graph', gate, activations },
      // Het mini-graph-icoon onderscheidt deze tab van een gewone gate-info-tab
      // met hetzelfde poortnummer als label.
      dockTab: { label: `${gate}`, icon: 'circle-nodes' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
  }
}
